/**
 * How each STAGE looks as a badge. Sibling of `dealflow-card-actions.ts`, which
 * does the same job for `DealAction` — presentation only; which stages exist is
 * decided by `src/lib/dealflow/stage.ts`.
 *
 * TOTAL OVER `DealStage`, DELIBERATELY. This map used to be built inline in
 * each screen and typed `Record<string, …>` with an `as` cast, so adding
 * `awaiting_access` to the spine did not break a single compile — it produced a
 * runtime `undefined`, and `statusConfig[deal.status].label` would have thrown a
 * TypeError and crashed the card for any deal at the new stage. Typed against
 * `DealStage`, a stage added without a badge is a COMPILE error, in every screen
 * at once. Do not widen this back to `Record<string, …>` and do not reintroduce
 * the cast.
 */
import type { useThemeColors } from '@/lib/useThemeColors';
import type { DealStage } from '@/lib/dealflow/stage';

export interface DealStatusBadge {
  color: string;
  bg: string;
  label: string;
}

/** Total by construction — see the note above. */
export type DealStatusConfig = Record<DealStage, DealStatusBadge>;

type Colors = ReturnType<typeof useThemeColors>;

/** The pipeline badges. Memoize per theme; do not build in render. */
export function buildDealStatusConfig(colors: Colors): DealStatusConfig {
  return {
    screening: { color: colors.primary[600], bg: colors.primary[50], label: 'Screening' },
    interested: { color: colors.success, bg: colors.traffic.greenLight, label: 'Interested' },
    // The wait on the founder is what an investor most wants to see at a
    // glance, so it gets its own badge rather than reading as "Interested".
    awaiting_access: { color: colors.warning, bg: colors.amber.light, label: 'Awaiting Access' },
    diligence: { color: colors.warning, bg: colors.amber.light, label: 'In Diligence' },
    invested: { color: colors.primary[600], bg: colors.primary[50], label: 'Invested' },
    rejected: { color: colors.text.tertiary, bg: colors.light[300], label: 'Rejected' },
    monitoring: { color: colors.text.secondary, bg: colors.light[300], label: 'Monitoring' },
  };
}

/**
 * Raising-feed items always carry status 'screening' — badge that one as
 * "Raising". Built by overriding the full map rather than returning a
 * single-key object, so this stays total and a new stage cannot slip past it.
 */
export function buildRaisingStatusConfig(colors: Colors): DealStatusConfig {
  return {
    ...buildDealStatusConfig(colors),
    screening: { color: colors.success, bg: colors.traffic.greenLight, label: 'Raising' },
  };
}
